import type { ConceptFinding } from "../api/contentRegistryApi";
import { plainText } from "../utils";
import { SectionCard } from "../Domain/domainIntelligenceParts";
import { EmptySlot } from "./conceptIntelligenceParts";
import {
  RECURRING_BAR_CLS,
  RECURRING_PILL_CLS,
  RECURRING_UNLINKED_FLAG,
  RECURRING_UNLINKED_SUFFIX,
  recurringUnlinkedSuffix,
} from "./utilities";

export interface RecurringThemesSectionProps {
  sortedRecurring: ConceptFinding[];
  recurringMax: number;
  groupingPass?: string;
}

interface RecurringRowProps {
  finding: ConceptFinding;
  recurringMax: number;
}

function RecurringRow({ finding, recurringMax }: RecurringRowProps) {
  const occurrences = finding.occurrences ?? 0;
  const denom = recurringMax > 0 ? recurringMax : 1;
  const pct = (occurrences / denom) * 100;
  const occurrenceLabel = occurrences === 1 ? "occurrence" : "occurrences";

  return (
    <li className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2 min-w-0">
      <span className={RECURRING_PILL_CLS}>Recurring</span>
      <span className="text-[13px] font-medium text-foreground min-w-0 break-words flex-1 sm:flex-none sm:max-w-[42%]">
        {plainText(finding.name)}
      </span>
      <div className="relative h-1.5 flex-1 min-w-[60px] rounded-full bg-muted overflow-hidden">
        <div
          className={RECURRING_BAR_CLS}
          style={{ width: `${Math.round(pct)}%` }}
        />
      </div>
      <span className="text-[11.5px] font-semibold text-foreground tabular-nums whitespace-nowrap">
        {occurrences.toLocaleString()} {occurrenceLabel}
      </span>
    </li>
  );
}

export function RecurringThemesSection({
  sortedRecurring,
  recurringMax,
  groupingPass,
}: RecurringThemesSectionProps) {
  const unlinked = recurringUnlinkedSuffix(
    groupingPass,
    RECURRING_UNLINKED_FLAG,
    RECURRING_UNLINKED_SUFFIX,
  );
  const rightLabel = `${sortedRecurring.length} ranked by occurrence${unlinked}`;

  return (
    <SectionCard headerLabel="Recurring themes" rightLabel={rightLabel}>
      <div className="p-3">
        {sortedRecurring.length === 0 ? (
          <EmptySlot label="recurring themes" />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {sortedRecurring.map((finding) => (
              <RecurringRow
                key={finding.concept_id || finding.normalized_name || finding.name}
                finding={finding}
                recurringMax={recurringMax}
              />
            ))}
          </ul>
        )}
      </div>
    </SectionCard>
  );
}
